import React, { useState } from 'react';
import styled from 'styled-components';
import MobileSubNavigator from '../components/MobileSubNavigator';
import { getHomeLinks } from '../utils/homeLinks';
import { tokens } from '../components/admin/AdminUI';

const c = tokens.color;

const Container = styled.div`
  width: 100%;
  padding: 16px;
  background: ${c.appBg};
  min-height: 100vh;
  box-sizing: border-box;
  font-family: ${tokens.font};
  color: ${c.text};
`;

const Title = styled.h1`
  font-size: 22px;
  color: ${c.heading};
  margin: 4px 0 20px 0;
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const GuideSection = styled.div`
  background: ${c.surface};
  border: 1px solid ${c.border};
  border-radius: ${tokens.radius.lg};
  margin-bottom: 12px;
  box-shadow: ${tokens.shadow.card};
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: 16px;
  min-height: 44px;
  background: ${c.surface};
  color: ${c.heading};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color .15s;

  &:hover {
    background: ${c.surfaceMuted};
  }
`;

const Chevron = styled.span`
  color: ${c.subtle};
  font-size: 12px;
  transition: transform 0.2s ease;
  transform: ${props => props.expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const SectionContent = styled.div`
  padding: ${props => props.expanded ? '4px 16px 12px' : '0 16px'};
  max-height: ${props => props.expanded ? '1000px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  border-top: ${props => props.expanded ? `1px solid ${c.borderSubtle}` : '1px solid transparent'};
`;

const GuideItem = styled.div`
  padding: 14px 0;
  border-bottom: 1px solid ${c.borderSubtle};

  &:last-child {
    border-bottom: none;
  }
`;

const GuideTitle = styled.h4`
  font-size: 14px;
  color: ${c.heading};
  margin: 0 0 6px 0;
  font-weight: 600;
`;

const GuideDescription = styled.p`
  font-size: 13px;
  color: ${c.muted};
  margin: 0;
  line-height: 1.5;
`;

const MobileSystemGuide = () => {
  const [expandedSections, setExpandedSections] = useState([]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => {
      if (prev.includes(sectionId)) {
        return prev.filter(id => id !== sectionId);
      } else {
        return [...prev, sectionId];
      }
    });
  };

  const guideData = [
    {
      id: 'basic',
      title: '기본 사용법',
      items: [
        {
          title: '로그인 및 회원가입',
          description: '시스템에 접속하여 로그인하는 방법과 새 계정을 만드는 절차를 안내합니다.'
        },
        {
          title: '메뉴 탐색',
          description: '각 메뉴의 기능과 사용법, 페이지 간 이동 방법을 설명합니다.'
        }
      ]
    },
    {
      id: 'board',
      title: '게시판 관리',
      items: [
        {
          title: '게시글 작성',
          description: '새로운 게시글을 작성하고 등록하는 방법을 설명합니다.'
        },
        {
          title: '게시판 설정',
          description: '게시판 권한 설정, 카테고리 관리 등 관리자 기능을 안내합니다.'
        }
      ]
    },
    {
      id: 'system',
      title: '시스템 설정',
      items: [
        {
          title: '사용자 권한 관리',
          description: '사용자별 권한 설정과 그룹 관리 방법을 설명합니다.'
        },
        {
          title: '시스템 모니터링',
          description: '시스템 상태 확인과 성능 모니터링 방법을 안내합니다.'
        }
      ]
    }
  ];

  return (
    <MobileSubNavigator
      links={getHomeLinks()}
      sectionTitle="시스템 메뉴얼"
      backPath="/"
      showBackButton={false}
    >
      <Container>
        <Title>시스템 사용 가이드</Title>

        {guideData.map((section) => (
          <GuideSection key={section.id}>
            <SectionHeader onClick={() => toggleSection(section.id)}>
              <span>{section.title}</span>
              <Chevron expanded={expandedSections.includes(section.id)}>▾</Chevron>
            </SectionHeader>

            <SectionContent expanded={expandedSections.includes(section.id)}>
              {section.items.map((item, index) => (
                <GuideItem key={index}>
                  <GuideTitle>{item.title}</GuideTitle>
                  <GuideDescription>{item.description}</GuideDescription>
                </GuideItem>
              ))}
            </SectionContent>
          </GuideSection>
        ))}
      </Container>
    </MobileSubNavigator>
  );
};

export default MobileSystemGuide;
